import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress
} from '@mui/material';
import { useNotification } from '../../context/NotificationContext';

function Manage2FADialog({ open, onClose, user, onUserUpdated }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [setupInfo, setSetupInfo] = useState(null); // { method, qrCode, secret, destination }
  const { showNotification } = useNotification();

  // The backend 2FA setup endpoints operate on the logged-in user: setup is
  // self-service only (the second factor belongs to the account owner).
  // For other users, admins get a "clear 2FA" recovery action instead.
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSelf = user && (user._id || user.id) === currentUser._id;

  useEffect(() => {
    if (user) {
      setSelectedUser({ ...user }); // Work with a local copy
      setVerificationCode(''); // Reset verification code input
      setSetupInfo(null);
      setError(null); // Clear errors
    } else {
      setSelectedUser(null);
    }
  }, [user, open]); // Reset when user or open state changes

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Setup 2FA (self only)
  const handle2FASetup = async (method) => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/setup-2fa`, {
        method
      }, {
        headers: getAuthHeader()
      });

      setSetupInfo({
        method,
        qrCode: response.data.qrCode, // data-URL image (app method)
        secret: response.data.secret, // manual-entry TOTP secret (app method)
        destination: response.data.destination // masked email/phone (email/sms)
      });

      showNotification(response.data.message || `2FA setup initiated using ${method}`, 'success');
    } catch (err) {
      console.error('Error setting up 2FA:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to setup 2FA. Please try again.';
      setError(errorMessage);
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Verify 2FA setup (self only)
  const handleVerify2FA = async () => {
    if (!selectedUser || !verificationCode) return;
    try {
      setLoading(true);
      setError(null);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/verify-setup-2fa`, {
        code: verificationCode.trim()
      }, {
        headers: getAuthHeader()
      });

      const updatedUser = {
        ...selectedUser,
        twoFactorAuth: {
          method: setupInfo?.method,
          enabled: true,
          verified: true
        }
      };
      setSelectedUser(updatedUser);
      setSetupInfo(null);
      setVerificationCode('');
      onUserUpdated(updatedUser); // Notify parent component
      showNotification('2FA enabled successfully!', 'success');
    } catch (err) {
      console.error('Error verifying 2FA:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to verify 2FA code. Please check the code and try again.';
      setError(errorMessage);
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Disable 2FA (self) / Clear 2FA (admin, for another user)
  const handleDisable2FA = async () => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      setError(null);

      if (isSelf) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/disable-2fa`, {}, {
          headers: getAuthHeader()
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/admin/clear-2fa/${selectedUser._id || selectedUser.id}`, {}, {
          headers: getAuthHeader()
        });
      }

      const updatedUser = {
        ...selectedUser,
        twoFactorAuth: {
          enabled: false,
          verified: false,
          method: undefined
        }
      };
      setSelectedUser(updatedUser);
      setSetupInfo(null);
      onUserUpdated(updatedUser); // Notify parent component
      showNotification('2FA disabled successfully!', 'success');
    } catch (err) {
      console.error('Error disabling 2FA:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to disable 2FA. Please try again.';
      setError(errorMessage);
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedUser) {
    return null;
  }

  const twoFactorAuth = selectedUser.twoFactorAuth || {};
  const twoFAEnabled = twoFactorAuth.enabled && twoFactorAuth.verified;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Two-Factor Authentication for {selectedUser.firstName}</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', mb: 2 }} />}
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {!isSelf ? (
          // Admin view of another user: status + recovery clear only
          <>
            <Typography variant="body1">
              {twoFAEnabled
                ? `2FA is enabled for this user (${twoFactorAuth.method || 'unknown method'}).`
                : '2FA is not enabled for this user.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              2FA setup is self-service — each user enables it from their own
              account. If this user is locked out of their second factor, you
              can clear their 2FA so they can log in and set it up again.
            </Typography>
            {twoFAEnabled && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDisable2FA}
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Clear 2FA for this user
              </Button>
            )}
          </>
        ) : twoFAEnabled ? (
          // 2FA is Enabled (self)
          <>
            <Typography variant="body1">
              2FA is currently enabled using {twoFactorAuth.method || 'an unknown method'}.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDisable2FA}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              Disable 2FA
            </Button>
          </>
        ) : setupInfo ? (
          // 2FA Setup Initiated, Pending Verification (self)
          <>
            <Typography variant="body1">
              2FA setup initiated using {setupInfo.method}. Please verify to enable.
            </Typography>
            {setupInfo.method === 'app' && setupInfo.qrCode && (
              <>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, ...):
                </Typography>
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img src={setupInfo.qrCode} alt="2FA QR Code" />
                </Box>
                <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                  Or enter this key manually: <b>{setupInfo.secret}</b>
                </Typography>
              </>
            )}
            {(setupInfo.method === 'email' || setupInfo.method === 'sms') && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                A verification code was sent to {setupInfo.destination || `your ${setupInfo.method === 'sms' ? 'phone' : 'email'}`}.
                {setupInfo.method === 'email' ? " If you don't see it, check your spam folder." : ''}
              </Typography>
            )}
            <TextField
              margin="dense"
              label="Verification Code"
              fullWidth
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleVerify2FA}
              sx={{ mt: 2 }}
              disabled={loading || !verificationCode}
            >
              Verify & Enable 2FA
            </Button>
            <Button
              variant="outlined"
              onClick={() => handle2FASetup(setupInfo.method)} // Resend/Regenerate
              sx={{ mt: 2, ml: 1 }}
              disabled={loading}
            >
              {setupInfo.method === 'app' ? 'Regenerate QR Code' : 'Resend Code'}
            </Button>
          </>
        ) : (
          // 2FA Not Setup (self)
          <>
            <Typography variant="body1">
              Two-Factor Authentication is optional. Choose a method to set it up:
            </Typography>
            <Button
              variant="contained"
              onClick={() => handle2FASetup('app')}
              sx={{ mt: 2, mr: 1 }}
              disabled={loading}
            >
              Authenticator App
            </Button>
            <Button
              variant="contained"
              onClick={() => handle2FASetup('email')}
              sx={{ mt: 2, mr: 1 }}
              disabled={loading}
            >
              Email
            </Button>
            <Button
              variant="contained"
              onClick={() => handle2FASetup('sms')}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              SMS
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default Manage2FADialog;
